import { Link } from "react-router-dom";
import { useFacilityCommunity } from "../hooks/useFacilityCommunity";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * A SECONDARY section on the drop-in/facility detail page that surfaces THIS
 * facility's community ("club") if one exists, routing into the existing
 * /community/communities/:communityId page (which owns join/leave + the feed).
 * Thin presentation — no join logic lives here; the label is context-aware
 * (Join vs View) while the href is identical.
 *
 * It must NEVER gate the primary drop-in flow:
 *   • loading            → render nothing (no skeleton that implies a community
 *                          may exist for a facility that has none).
 *   • read resolves null → render nothing (200 + null body, not a 404; no empty
 *                          card).
 *   • read rejects       → render nothing (no role="alert" — a secondary section
 *                          never breaks the primary flow with an error banner).
 * Only when `data` is a community object does the section render.
 *
 * No raw UID/UUID leaks: created_by / owner_facility_id / community_id are never
 * rendered as visible text — community_id appears only inside the Link href.
 * The member count is FIELD-ONLY, mirroring CommunityDetailPage exactly: a real
 * 0 → "0 members"; absent / non-number → "— members". Never a fabricated count.
 */
export function FacilityCommunityCard({ facilityId }: { facilityId: string }) {
  const communityQ = useFacilityCommunity(facilityId);

  // Loading, error, and "no community" (null body) all render nothing — a
  // secondary section never shows a skeleton, an alert, or an empty card.
  if (communityQ.isLoading || communityQ.isError || !communityQ.data) {
    return null;
  }

  const community = communityQ.data;

  // A community with no usable name would render a blank-titled section next to a
  // live Join link — a dishonest surface. Treat a name-less community like null
  // (render nothing), mirroring the field-only member_count guard. (#1452)
  const name = typeof community.name === "string" ? community.name.trim() : "";
  if (!name) return null;

  return (
    <Box component="section" aria-labelledby="sec-community">
      <Typography variant="overline" color="primary.main">
        This facility&apos;s club
      </Typography>
      <Typography component="h2" variant="h5" id="sec-community">
        Community
      </Typography>
      <Stack spacing={1} sx={{ mt: 1, alignItems: "flex-start" }}>
        <Typography sx={{ fontWeight: 800 }}>{name}</Typography>
        <Typography color="text.secondary">
          {typeof community.member_count === "number"
            ? `${community.member_count} members`
            : "— members"}
        </Typography>
        <Button
          component={Link}
          to={`/community/communities/${community.community_id}`}
          variant="contained"
        >
          {community.is_member ? "View community" : "Join community"}
        </Button>
      </Stack>
    </Box>
  );
}
